<template>
  <b-card
    ref="post"
    class="card-box card__post card__post--task"
    body-class="pt-3"
    :class="{ 'card__post-denied': isDeniedPost }"
  >
    <div class="container__task-content">
      <div class="d-flex align-items-baseline justify-content-between">
        <b class="text__task-title">
          {{ post.title }}
        </b>

        <base-ellipsis
          v-if="editable"
          ref="icon__dropdown-menu"
          class="icon__dropdown-menu"
          :disabled="disabled"
        >
          <b-dropdown-item
            ref="dropdown-menu__edit"
            class="dropdown-menu__edit"
            :to="editPostEvaluationAssessmentPage"
          >
            Edit
          </b-dropdown-item>

          <b-dropdown-item
            v-if="!hideDelete"
            ref="dropdown-menu__delete"
            class="dropdown-menu__delete"
            @click="promptDelete"
          >
            Delete
          </b-dropdown-item>
        </base-ellipsis>
      </div>

      <modal-delete-post
        :post="post"
        :show.sync="showModalDeletePost"
        :title="modalDeletePostTitle"
        :sub-title="modalDeletePostSubTitle"
        :description="modalDeletePostDescription"
      >
      </modal-delete-post>

      <base-hide-content-wrapper
        class="mt-3 text__task-description"
        :active="activeHideContent"
        :enable-fade="isDeniedPage ? false : true"
      >
        <b v-if="post.word_limit" class="text__task-word-limit">
          {{ post.word_limit }}-word limit
        </b>

        <base-text-editor-display>
          <div
            v-safe-html="post.description"
            class="text-pre-wrap p-no-mb text__task-description-markdown"
          />
        </base-text-editor-display>
      </base-hide-content-wrapper>

      <!-- for designer and learner -->
      <div
        v-if="!isDeniedPage && (!mode || mode === 'preview')"
        class="mt-3 container__task-assessment-rubric-for-designer-learner"
      >
        <criterion-evaluation-assessment-form
          v-for="criterion in criteria"
          :key="`post-${post.id}--criterion-${criterion.id}`"
          head-title="Assessment Rubric"
          :criterion="criterion"
        />
      </div>

      <div
        v-if="post.attachments && post.attachments.length"
        class="mt-3 container__task-assesment-attachments"
      >
        <base-files-list :attachments="post.attachments"></base-files-list>
      </div>

      <div v-if="submittable" class="mt-4 container__task-assessment-submission">
        <post-response-form
          v-if="post && evidenceSubmission && assessmentSubmission"
          ref="response-form"
          :post="post"
          :evidence-submission="evidenceSubmission"
          :post-response="assessmentSubmission"
          :disabled="disabled"
          :header-title="headerTitle"
          :readonly="isReadOnly"
          :part="part"
          :order="order"
        ></post-response-form>
      </div>

      <!-- for assessor and issuer -->
      <div
        v-if="!isDeniedPage && mode && mode !== 'preview'"
        class="mt-3 container__task-assessment-rubric-for-assessor-issuer"
      >
        <CriteriaAssessmentRubic
          v-for="criterion in criteria"
          :key="`post-${post.id}--criterion-${criterion.id}`"
          :criterion="criterion"
        />
      </div>

      <div v-if="assessable" class="mt-4 container__task-assessment-evaluation">
        <post-response-form
          v-if="post && evidenceSubmission && assessmentEvaluation"
          :post="post"
          :evidence-submission="evidenceSubmission"
          :post-response="assessmentEvaluation"
          :disabled="disabled"
          :criteria="criteria"
          :header-title="assessableTitle"
          :mode="mode"
          :readonly="readonly ? readonly : !evidenceSubmission.isReviewPending"
          :evidence-submitting="evidenceSubmitting"
          :allow-assess="allowAssess"
          :hide-comment="hideComment"
          :part="part"
          :order="order"
          :show-all-criteria="!isDeniedPage"
        ></post-response-form>
      </div>
    </div>
  </b-card>
</template>

<script>
import EvidenceSubmission from '@/models/EvidenceSubmission';
import Criteria from '@/models/Criteria';
import Post from '@/models/Post';
import PostResponse from '@/models/PostResponse';
import BaseEllipsis from '@/components/base-components/BaseEllipsis';
import BaseHideContentWrapper from '@/components/base-components/BaseHideContentWrapper';
import BaseFilesList from '@/components/base-components/BaseFilesList';
import CriterionEvaluationAssessmentForm from '@/components/Criteria/CriterionEvaluationAssessmentForm.vue';
import PostResponseForm from './PostResponseForm';
import ModalDeletePost from '@/views/Post/components/ModalDeletePost';
import BaseTextEditorDisplay from '@/components/base-components/BaseTextEditorDisplay';
import CriteriaAssessmentRubic from '@/components/Criteria/CriteriaAssessmentRubic.vue';

export default {
  components: {
    BaseEllipsis,
    BaseHideContentWrapper,
    BaseFilesList,
    BaseTextEditorDisplay,
    CriterionEvaluationAssessmentForm,
    PostResponseForm,
    ModalDeletePost,
    CriteriaAssessmentRubic
  },
  props: {
    post: {
      type: Post,
      required: true
    },

    part: {
      type: String,
      required: true
    },

    order: {
      type: Number,
      default: 0
    },

    disabled: {
      type: [Number, Boolean],
      default: false
    },

    editable: {
      type: Boolean,
      default: false
    },

    submittable: {
      type: Boolean,
      default: false
    },

    assessable: {
      type: Boolean,
      default: false
    },

    evidenceSubmitting: {
      type: Boolean,
      default: false
    },

    evidenceSubmission: {
      type: EvidenceSubmission,
      default: () => null
    },

    mode: {
      type: String,
      default: ''
    },

    readonly: {
      type: [Number, Boolean],
      default: false
    },

    allowAssess: {
      type: Boolean,
      default: false
    },

    hideComment: {
      type: Boolean,
      default: false
    },

    hideDelete: {
      type: Boolean,
      default: false
    },

    isDeniedPage: {
      type: Boolean,
      default: false
    },
    assessableTitle: {
      type: String,
      default: 'Comment'
    }
  },

  data() {
    return {
      activeHideContent: true,
      showModalDeletePost: false,
      modalDeletePostTitle: 'Delete this evaluation assessment',
      modalDeletePostSubTitle: 'Are you sure that you want to delete this evaluation assessment?',
      modalDeletePostDescription: 'Click at the "Delete" button to delete this post.'
    };
  },

  computed: {
    editPostEvaluationAssessmentPage() {
      return {
        name: 'learning-unit/evaluation-assessment/edit',
        params: {
          learningUnitId: this.post.learning_unit_id,
          part: this.part.toLowerCase(),
          postId: this.post.id
        },
        query: {
          previousPath: this.$route.fullPath
        }
      };
    },

    criteria() {
      const { criterion_ids = [] } = this.post;

      return Criteria.query().whereIdIn(criterion_ids).with('additional_criteria').get();
    },

    assessmentSubmission() {
      return this.evidenceSubmission
        ? PostResponse.query()
            .where('evidence_submission_id', this.evidenceSubmission.id)
            .where('assessment_submission', (value) => value !== null)
            .where('post_id', this.post.id)
            .first()
        : null;
    },

    assessmentEvaluation() {
      return this.evidenceSubmission
        ? PostResponse.query()
            .where('evidence_submission_id', this.evidenceSubmission.id)
            .where('assessment_evaluation_id', (value) => value !== null)
            .where('post_id', this.post.id)
            .with('assessment_evaluation')
            .first()
        : null;
    },
    headerTitle() {
      return this.mode === 'review'
        ? 'Evidence'
        : this.mode === 'preview'
        ? 'Response'
        : 'Evidence';
    },
    isReadOnly() {
      return ['review', 'preview', 'assess'].includes(this.mode)
        ? true
        : !this.evidenceSubmission.isNotSubmitted;
    },
    isDeniedPost() {
      return this.isDeniedPage && this.assessmentEvaluation.assessment_evaluation?.pass === false;
    }
  },

  methods: {
    promptDelete() {
      this.showModalDeletePost = true;
    }
  }
};
</script>

<style scoped lang="scss">
.card__post {
  max-width: 910px;
  margin: auto;
}

.card__post-denied {
  border: 1px solid #f83245;
  background-color: #fff5f6;
}
</style>
